// The example CSV the Import menu hands out. Its shape and its cell formats come
// from the legacy importer's own validator (git history: 592b5417,
// `EditDataSourceHeader.validateCSV` / `validateDataRows`), so a user who fills
// this in gets a file the backend already knows how to read:
//
//   row 1   column names
//   row 2   their dataTypes
//   row 3+  the records — legacy required AT LEAST ONE, hence three here
//
// Per-type cell formats:
//   text            anything
//   number          must parse as a Number
//   dateTime        `isValidMMDDYYYY` — MM/DD/YYYY, zero-padded, and a date that
//                   exists (02/30/2026 fails; February is leap-aware). Date
//                   only: a time component does not validate.
//   geoCoordinates  `geoCoordinatesRegex` — "lat, lng", comma AND space, positive
//                   only. The comma is why those cells come out quoted, which the
//                   template usefully demonstrates.
//   phoneNumber     `phoneRegex` — "+<code>-<number>". The HYPHEN is required, so
//                   an unbroken +966551234567 would not validate.
//
// The two regexes and the date check are the importer's, and are exported from
// parseDataSourceCsv so the samples below and the rules that will check them
// cannot drift apart. Every sample here has been run against them.
//
// The contents are DATA, not copy — a filled-in template is written to the
// backend verbatim — so every value here stays literal English and none of it
// goes through i18n.

import { COLUMN_TYPES } from './columnTypes';

export const TEMPLATE_FILE_NAME = 'data-source-template.csv';

type ColumnTypeValue = (typeof COLUMN_TYPES)[number]['value'];

/** One example column per supported dataType. Declared as a Record over the FULL
 *  union, so adding a type to COLUMN_TYPES fails the build here until it gets a
 *  sample — the template cannot silently fall behind the picker. */
const TEMPLATE_COLUMNS: Record<ColumnTypeValue, { name: string; samples: string[] }> = {
  text: {
    name: 'Site',
    samples: ['Riyadh Central Depot', 'Jeddah Port Warehouse', 'Dammam Cold Store'],
  },
  number: { name: 'Capacity', samples: ['1200', '860', '430'] },
  dateTime: { name: 'Opened', samples: ['06/24/2026', '01/15/2026', '11/03/2025'] },
  geoCoordinates: {
    name: 'Coordinates',
    samples: ['24.7136, 46.6753', '21.4858, 39.1925', '26.4207, 50.0888'],
  },
  phoneNumber: {
    name: 'Contact',
    // `+<code>-<number>` per phoneRegex — the hyphen is not decoration.
    samples: ['+966-551234567', '+966-552345678', '+966-553456789'],
  },
};

const SAMPLE_ROW_COUNT = 3;

/** RFC 4180: a cell needs quoting once it contains a comma, a quote or a line
 *  break, and an inner quote doubles. geoCoordinates values hit this every time. */
const escapeCell = (value: string): string =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const toRow = (cells: string[]): string => cells.map(escapeCell).join(',');

/**
 * Build the example CSV. Columns follow COLUMN_TYPES order, so the file reads in
 * the same order the Add-column picker offers.
 *
 * CRLF line endings: Excel is the overwhelmingly likely destination and it is the
 * RFC's own choice. Our parser (and papaparse generally) reads either.
 */
export const buildDataSourceTemplateCsv = (): string => {
  const columns = COLUMN_TYPES.map((type) => ({
    type: type.value,
    ...TEMPLATE_COLUMNS[type.value],
  }));
  const rows = [
    toRow(columns.map((column) => column.name)),
    toRow(columns.map((column) => column.type)),
    ...Array.from({ length: SAMPLE_ROW_COUNT }, (_, index) =>
      toRow(columns.map((column) => column.samples[index] ?? '')),
    ),
  ];
  return `${rows.join('\r\n')}\r\n`;
};

// ---- entries template (the entries page's "Download this file") ----

/** The id column prepended to an entries template. It is how a row identifies an
 *  EXISTING record; the type is `text` so it passes the importer's whitelist. */
export const ENTRY_ID_COLUMN = '_id';

/** `Branches` → `branches-template.csv`. Falls back to the generic name when the
 *  source's name slugs to nothing (all punctuation, or not yet loaded). */
export const entriesTemplateFileName = (schemaName: string): string => {
  const slug = schemaName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `${slug}-template.csv` : TEMPLATE_FILE_NAME;
};

/**
 * The entries template: `_id` plus THIS data source's own columns and their
 * types, and **no data rows** — the user fills the records in themselves.
 *
 * Deliberately empty per the product decision. One consequence worth knowing:
 * the file cannot be re-uploaded untouched, because the importer requires at
 * least one record (`no_data_rows`). That is intended — importing nothing is a
 * mistake, not a way to clear a file.
 */
export const buildEntriesTemplateCsv = (
  columns: { columnName: string; dataType: string }[],
): string => {
  const names = [ENTRY_ID_COLUMN, ...columns.map((column) => column.columnName)];
  const types = ['text', ...columns.map((column) => column.dataType)];
  return `${toRow(names)}\r\n${toRow(types)}\r\n`;
};
